import { Add as AddIcon, DeleteOutline } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { filter, map, some } from 'lodash';
import React, { useContext, useRef, useState } from 'react';

import { ProjectContext } from '../../../Contexts/Project/Context';

import { DEFAULT_COUNTRY } from './constants';
import SiteAddressForm from './SiteAddressForm';
import { isAddressComplete } from './validation';

import { type AddressInput, AddressLocationTypeEnum, type Address } from '@/graphql/generated/graphql';
import { arrayLength, formatAddress } from '@/utils/helperFunctions';

const MultipleAddressesForm: React.FC = () => {
  const { parentProject, project, onAddressesChange } = useContext(ProjectContext);
  const [selectedOptions, setSelectedOptions] = useState<AddressInput[]>([]);
  const addresses = project.addresses || [];

  // Stable identities for list rendering so add/remove doesn't remount
  // (and collapse) every accordion
  const keyCounter = useRef(0);
  const keysRef = useRef<number[]>([]);
  while (keysRef.current.length < addresses.length) keysRef.current.push(keyCounter.current++);
  if (keysRef.current.length > addresses.length) keysRef.current.length = addresses.length;

  const addressesOptions: AddressInput[] = parentProject
    ? [
        ...(parentProject.mainAddress
          ? [
              {
                street: parentProject.mainAddress.street,
                city: parentProject.mainAddress.city,
                state: parentProject.mainAddress.state,
                country: parentProject.mainAddress.country,
                zip: parentProject.mainAddress.zip,
                locationType: AddressLocationTypeEnum.Main,
              },
            ]
          : []),
        ...(parentProject.subLocations?.map((subLocation) => ({
          street: subLocation.street,
          city: subLocation.city,
          state: subLocation.state,
          country: subLocation.country,
          zip: subLocation.zip,
          locationType: AddressLocationTypeEnum.Sub,
        })) || []),
      ]
    : [];

  const handleAddAddress = () => {
    keysRef.current.push(keyCounter.current++);
    onAddressesChange([
      ...addresses,
      {
        street: '',
        city: '',
        state: '',
        country: DEFAULT_COUNTRY,
        zip: '',
        locationType: arrayLength(addresses) === 0 ? AddressLocationTypeEnum.Main : AddressLocationTypeEnum.Sub,
      },
    ]);
  };

  const handleRemoveAddress = (index: number) => {
    const addressToRemove = addresses[index];
    const isMainAddressRemoved = addressToRemove?.locationType === AddressLocationTypeEnum.Main;

    const newAddresses = [...addresses];
    newAddresses.splice(index, 1);
    keysRef.current = [...keysRef.current.slice(0, index), ...keysRef.current.slice(index + 1)];

    // Remove from selected options if present
    const newSelectedOptions = selectedOptions.filter(
      (option) =>
        !(
          option.street === addressToRemove.street &&
          option.city === addressToRemove.city &&
          option.zip === addressToRemove.zip
        ),
    );

    // Set the first address locationType to Main after removal
    if (isMainAddressRemoved && arrayLength(newAddresses) > 0) {
      newAddresses[0] = {
        ...newAddresses[0],
        locationType: AddressLocationTypeEnum.Main,
      };
    }

    onAddressesChange(newAddresses);
    setSelectedOptions(newSelectedOptions);
  };

  const handleAddressChange = (index: number, key: string, value: string) => {
    const newAddresses = map(addresses, (address, i) => {
      if (i === index) {
        return { ...address, [key]: value };
      }
      return address;
    });
    onAddressesChange(newAddresses);
  };

  const handleAddSelectedAddresses = (selected: AddressInput[]) => {
    // First, filter out any empty addresses from the existing list
    const nonEmptyAddresses = filter(
      addresses,
      (addr) =>
        addr.street.trim() !== '' || addr.city.trim() !== '' || addr.state.trim() !== '' || addr.zip.trim() !== '',
    );

    // Check if there's already a main address in the non-empty addresses
    const hasMainAddress = some(nonEmptyAddresses, (addr) => addr.locationType === AddressLocationTypeEnum.Main);

    // Process new selected addresses
    const newAddresses = filter(
      selected,
      (option) =>
        !nonEmptyAddresses.some(
          (addr) => addr.street === option.street && addr.city === option.city && addr.zip === option.zip,
        ),
    ).map((option) => ({
      ...option,
      locationType: hasMainAddress ? AddressLocationTypeEnum.Sub : option.locationType,
    }));

    const combined = [...nonEmptyAddresses, ...newAddresses];
    keysRef.current = combined.map(() => keyCounter.current++);
    onAddressesChange(combined);
    setSelectedOptions([]);
  };

  const hasIncompleteAddress = some(addresses, (addr) => !isAddressComplete(addr));

  return (
    <Grid>
      {parentProject && arrayLength(addressesOptions) > 0 && (
        <Grid size={12} mb={2}>
          <Autocomplete
            multiple
            options={addressesOptions}
            getOptionLabel={(option) => formatAddress(option as Address)}
            value={selectedOptions}
            onChange={(_, value) => {
              setSelectedOptions(value || []);
            }}
            isOptionEqualToValue={(option, value) =>
              option.street === value.street && option.city === value.city && option.zip === value.zip
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add addresses from parent project (Optional)"
                placeholder="Select addresses..."
              />
            )}
            renderTags={(value, getTagProps) =>
              map(value, (option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option.street}-${option.zip}`}
                  label={formatAddress(option as Address)}
                />
              ))
            }
          />
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleAddSelectedAddresses(selectedOptions)}
            disabled={selectedOptions.length === 0}
            sx={{ mt: 1 }}
          >
            Add Selected Addresses
          </Button>
        </Grid>
      )}

      {map(addresses, (address, index) => (
        <Accordion key={keysRef.current[index]} defaultExpanded={!isAddressComplete(address)}>
          <AccordionSummary>
            <Typography>
              {address.locationType === AddressLocationTypeEnum.Main ? 'Primary Address' : `Address ${index + 1}`}
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveAddress(index);
              }}
              onFocus={(e) => e.stopPropagation()}
              color="error"
              size="small"
              aria-label={`Remove address ${index + 1}`}
              sx={{ ml: 'auto' }}
            >
              <DeleteOutline />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            <SiteAddressForm
              onAddressChange={(key, value) => handleAddressChange(index, key, value)}
              address={address}
            />
          </AccordionDetails>
        </Accordion>
      ))}

      <Grid container spacing={2} mt={2} size={12}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            fullWidth
            type="button"
            sx={{ mt: 2 }}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddAddress}
            disabled={hasIncompleteAddress}
          >
            {arrayLength(addresses) > 1 ? 'Add Another Address' : 'Add Address'}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MultipleAddressesForm;
